import { useState } from "react";
import { X, Cookie, Shield, Settings, ChevronDown, ChevronUp } from "lucide-react";
import { getConsent, saveConsent } from "../cookieConsent";
import "../styles/CookieBanner.css";

const CATEGORIES = [
  {
    id: "essential",
    label: "Essential Storage",
    always: true,
    description:
      "Required for the site to work. Stores your login session so you stay signed in. Cannot be disabled.",
    examples: "Login session, security tokens",
  },
  {
    id: "functional",
    label: "Functional Storage",
    always: false,
    description:
      "Saves your game progress and caches generated question content so we don't re-generate the same questions repeatedly.",
    examples: "Game caches, question sets, progress state",
  },
  {
    id: "analytics",
    label: "Analytics Storage",
    always: false,
    description:
      "Helps us understand how the site is used so we can improve the experience. No personal data is shared with third parties.",
    examples: "Page views, feature usage patterns",
  },
];

export default function CookieBanner() {
  const existing = getConsent();

  const [visible, setVisible] = useState(!existing);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [prefs, setPrefs] = useState({
    functional: existing?.functional ?? true,
    analytics: existing?.analytics ?? false,
  });

  const accept = (all) => {
    saveConsent({ functional: all ? true : prefs.functional, analytics: all ? true : prefs.analytics });
    setVisible(false);
    setSettingsOpen(false);
  };

  const rejectAll = () => {
    saveConsent({ functional: false, analytics: false });
    setVisible(false);
    setSettingsOpen(false);
  };

  const savePrefs = () => {
    saveConsent(prefs);
    setVisible(false);
    setPrefsOpen(false);
    setSettingsOpen(false);
  };

  const togglePref = (id) => {
    setPrefs((p) => ({ ...p, [id]: !p[id] }));
  };

  const PreferencesPanel = ({ onClose }) => (
    <div className="cb-prefs-overlay" role="dialog" aria-modal="true" aria-label="Cookie preferences">
      <div className="cb-prefs-modal">
        <div className="cb-prefs-header">
          <div className="cb-prefs-title">
            <Settings size={18} /> Cookie &amp; Storage Preferences
          </div>
          <button className="cb-prefs-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="cb-prefs-body">
          <p className="cb-prefs-intro">
            We use browser storage (localStorage) rather than traditional cookies, but the
            same consent rules apply under UK PECR and GDPR. Choose what you allow below.
            You can change your preferences at any time using the cookie settings link at
            the bottom of the screen.
          </p>

          {CATEGORIES.map((cat) => (
            <div key={cat.id} className="cb-category">
              <div className="cb-category-header">
                <div className="cb-category-info">
                  <div className="cb-category-name">
                    <button
                      className="cb-category-expand"
                      onClick={() =>
                        setExpandedCategory(expandedCategory === cat.id ? null : cat.id)
                      }
                      aria-expanded={expandedCategory === cat.id}
                    >
                      {expandedCategory === cat.id ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </button>
                    <span className="cb-cat-label">{cat.label}</span>
                    {cat.always && (
                      <span className="cb-always-badge">Always active</span>
                    )}
                  </div>
                </div>
                {cat.always ? (
                  <div className="cb-toggle cb-toggle-locked" aria-label="Cannot be disabled">
                    <div className="cb-toggle-thumb" />
                  </div>
                ) : (
                  <button
                    role="switch"
                    aria-checked={prefs[cat.id]}
                    className={`cb-toggle ${prefs[cat.id] ? "cb-toggle-on" : ""}`}
                    onClick={() => togglePref(cat.id)}
                    aria-label={`${prefs[cat.id] ? "Disable" : "Enable"} ${cat.label}`}
                  >
                    <div className="cb-toggle-thumb" />
                  </button>
                )}
              </div>
              {expandedCategory === cat.id && (
                <div className="cb-category-detail">
                  <p>{cat.description}</p>
                  <p className="cb-examples"><strong>Examples:</strong> {cat.examples}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="cb-prefs-footer">
          <button className="cb-btn cb-btn-reject" onClick={() => { setPrefs({ functional: false, analytics: false }); saveConsent({ functional: false, analytics: false }); onClose(); }}>
            Reject Non-Essential
          </button>
          <button className="cb-btn cb-btn-save" onClick={savePrefs}>
            Save My Preferences
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* ── Main banner (shown until consent is given) ── */}
      {visible && (
        <div className="cb-banner" role="region" aria-label="Cookie consent">
          <div className="cb-banner-inner">
            <div className="cb-banner-content">
              <div className="cb-banner-icon">
                <Cookie size={22} />
              </div>
              <div className="cb-banner-text">
                <strong>We use browser storage on this site.</strong>{" "}
                Essential storage keeps you logged in. Optional storage saves your
                game progress and caches question content. You can accept all, reject
                non-essential, or{" "}
                <button
                  className="cb-inline-link"
                  onClick={() => setPrefsOpen(true)}
                >
                  manage your preferences
                </button>
                .{" "}
                <a
                  href="https://ico.org.uk/your-data-matters/online/cookies/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cb-inline-link"
                >
                  Learn more ↗
                </a>
              </div>
            </div>

            <div className="cb-banner-actions">
              <button
                className="cb-btn cb-btn-reject"
                onClick={rejectAll}
              >
                Reject Non-Essential
              </button>
              <button
                className="cb-btn cb-btn-prefs"
                onClick={() => setPrefsOpen(true)}
              >
                <Settings size={13} /> Manage
              </button>
              <button
                className="cb-btn cb-btn-accept"
                onClick={() => accept(true)}
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preferences modal (from banner or settings button) ── */}
      {prefsOpen && (
        <PreferencesPanel onClose={() => setPrefsOpen(false)} />
      )}
      {settingsOpen && (
        <PreferencesPanel onClose={() => setSettingsOpen(false)} />
      )}

      {/* ── Persistent settings trigger (after consent given) ── */}
      {!visible && (
        <button
          className="cb-settings-trigger"
          onClick={() => setSettingsOpen(true)}
          aria-label="Cookie settings"
          title="Cookie &amp; Storage Settings"
        >
          <Shield size={14} />
          <span>Cookie Settings</span>
        </button>
      )}
    </>
  );
}
